import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../data/mockData';
import { completeTour } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  LayoutGrid, BarChart3, Flag, Trophy, Watch,
  MessageCircle, Store, UserCircle, ArrowRight, X, Sparkles
} from 'lucide-react';

const TOUR_STEPS = [
  {
    title: 'Welcome to ThriveLonger.ai!',
    description: 'Let us show you around. This quick tour will help you discover all the powerful features available to optimize your health and longevity.',
    icon: Sparkles,
    color: COLORS.primary,
    position: 'center',
  },
  {
    title: 'Your Dashboard',
    description: 'This is your health command center. See your overall health score, active goals, membership status, and quick actions — all at a glance.',
    icon: LayoutGrid,
    color: COLORS.primary,
    highlight: '[data-testid="patient-dashboard"], .dashboard-content',
    navTo: '/dashboard',
  },
  {
    title: 'Health Insights',
    description: 'Dive deep into your 9-category health score: exercise, sleep, mental wellness, body composition, and more. Track your longevity estimate over time.',
    icon: BarChart3,
    color: '#4682B4',
    navTo: '/dashboard/insights',
  },
  {
    title: 'Health Goals',
    description: 'Set and track personalized health goals. Choose from templates or create your own. Categorize by diet, exercise, supplements, and more.',
    icon: Flag,
    color: '#3CB371',
    navTo: '/dashboard/goals',
  },
  {
    title: 'Community Challenges',
    description: 'Join challenges like 10K Steps, Sleep Champion, or Mindful Minutes. Compete on leaderboards and earn badges for completing challenges.',
    icon: Trophy,
    color: '#DAA520',
    navTo: '/dashboard/challenges',
  },
  {
    title: 'Wearable Integration',
    description: 'Connect your smartwatch or fitness tracker to sync heart rate, steps, HRV, sleep quality, and more. All your health data in one place.',
    icon: Watch,
    color: '#8B008B',
    navTo: '/dashboard/wearables',
  },
  {
    title: 'AI Health Coach',
    description: 'Chat with our GPT-powered AI coach for personalized health advice. Available with Elite membership — get evidence-based recommendations 24/7.',
    icon: MessageCircle,
    color: '#6B8E23',
    navTo: '/dashboard/chat',
  },
  {
    title: 'Health Store',
    description: 'Browse health devices, consultations, seminars, and more. From blood pressure monitors to nutrition consultations — everything you need.',
    icon: Store,
    color: '#B22222',
    navTo: '/dashboard/store',
  },
  {
    title: 'Your Profile',
    description: 'Manage your account settings, notification preferences, and membership. Update your health survey anytime to recalculate your scores.',
    icon: UserCircle,
    color: '#5C6BC0',
    navTo: '/dashboard/profile',
  },
  {
    title: 'Need Help Anytime?',
    description: 'Look for the help button (bottom-right corner) on any page. Our AI assistant can answer questions about features, navigation, and how to get the most out of ThriveLonger.ai.',
    icon: Sparkles,
    color: COLORS.primary,
    position: 'center',
  },
];

export default function GuidedTour({ onComplete }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const current = TOUR_STEPS[step];
  const Icon = current.icon;
  const isFirst = step === 0;
  const isLast = step === TOUR_STEPS.length - 1;

  const handleNext = useCallback(() => {
    if (isLast) {
      handleFinish();
      return;
    }
    const nextStep = TOUR_STEPS[step + 1];
    if (nextStep.navTo) navigate(nextStep.navTo);
    setStep(s => s + 1);
  }, [step, isLast, navigate]);

  const handleFinish = useCallback(async () => {
    setVisible(false);
    try {
      await completeTour();
      setUser(prev => prev ? { ...prev, tour_completed: true } : prev);
    } catch {}
    if (onComplete) onComplete();
    navigate('/dashboard');
  }, [onComplete, navigate, setUser]);

  const handleSkip = useCallback(async () => {
    setVisible(false);
    try {
      await completeTour();
      setUser(prev => prev ? { ...prev, tour_completed: true } : prev);
    } catch {}
    if (onComplete) onComplete();
  }, [onComplete, setUser]);

  if (!visible) return null;

  return (
    <div data-testid="guided-tour" style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Backdrop */}
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} />

      {/* Tour Card */}
      <div data-testid="tour-card" style={{
        position: 'relative', zIndex: 1, backgroundColor: '#FFF', borderRadius: 24,
        padding: 32, maxWidth: 480, width: '90%', boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
        animation: 'fadeInUp 0.4s ease-out',
      }}>
        {/* Skip button */}
        <button data-testid="tour-skip" onClick={handleSkip} style={{
          position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
          cursor: 'pointer', padding: 4, color: COLORS.textSecondary,
        }}>
          <X size={20} />
        </button>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginBottom: 24 }}>
          {TOUR_STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 24 : 8, height: 8, borderRadius: 4,
              backgroundColor: i === step ? current.color : `${COLORS.border}`,
              transition: 'all 0.3s ease',
            }} />
          ))}
        </div>

        {/* Icon */}
        <div style={{
          width: 72, height: 72, borderRadius: 20, backgroundColor: `${current.color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px', transition: 'background-color 0.3s',
        }}>
          <Icon size={36} color={current.color} />
        </div>

        {/* Content */}
        <h2 style={{
          fontSize: 22, fontWeight: 800, color: COLORS.textPrimary,
          textAlign: 'center', fontFamily: 'Outfit, sans-serif', marginBottom: 10,
        }}>
          {current.title}
        </h2>
        <p style={{
          fontSize: 15, color: COLORS.textSecondary, textAlign: 'center',
          lineHeight: 1.6, marginBottom: 28,
        }}>
          {current.description}
        </p>

        {/* Step counter */}
        <div style={{ fontSize: 13, color: COLORS.textSecondary, textAlign: 'center', marginBottom: 16 }}>
          Step {step + 1} of {TOUR_STEPS.length}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12 }}>
          {!isFirst && (
            <button data-testid="tour-prev" onClick={() => {
              const prevStep = TOUR_STEPS[step - 1];
              if (prevStep.navTo) navigate(prevStep.navTo);
              setStep(s => s - 1);
            }} style={{
              flex: 1, padding: '14px', borderRadius: 12, border: `1px solid ${COLORS.border}`,
              backgroundColor: '#FFF', color: COLORS.textSecondary, fontWeight: 600,
              fontSize: 15, cursor: 'pointer',
            }}>
              Back
            </button>
          )}
          <button data-testid="tour-next" onClick={handleNext} style={{
            flex: isFirst ? 1 : 2, padding: '14px', borderRadius: 12, border: 'none',
            backgroundColor: current.color, color: '#FFF', fontWeight: 700,
            fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8, transition: 'background-color 0.3s',
          }}>
            {isFirst ? "Let's Go!" : isLast ? 'Finish Tour' : 'Next'}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
